import { AxiosResponse } from "axios";
import { GigType, PaymentMethod } from "model/Gig";
import { TimeReportResolution, TimeReportStatusV2 } from "model/TimeReport";
import { API_ROOT } from "services/baseURLs.config";
import { axiosRequest } from "../api.utils";
import { AbsenceType } from "model/Schedule";

export type TimeReportsV2SortBy = "Worker" | "WorkGroup";
export type TimeReportsV2Filter =
  | "Open"
  | "Pending"
  | "Approved"
  | "Planned"
  | "Absence"
  | "Deviation";

export interface TimeReportsV2Item {
  id: number;
  companyUnitId: number;
  companyUnitName: string | null;
  workGroupId: number;
  workGroupName: string | null;
  status: TimeReportStatusV2;
  resolution: TimeReportResolution;
  reportedStartTime: string | null;
  reportedEndTime: string | null;
  breakStartTime: string | null;
  breakEndTime: string | null;
  stampedStartTime: string | null;
  stampedEndTime: string | null;
  plannedStartTime: string | null;
  plannedEndTime: string | null;
  plannedBreakStartTime?: string | null;
  plannedBreakEndTime?: string | null;
  updatedAt: string;
}

export interface TimeReportsV2GroupDetails {
  timeReportCount?: number;
  planned?: string;
  approved?: string;
  pending?: string;
  open?: string;
  absence?: string;
  deviation?: string;
}

export interface TimeReportsV2Group {
  id: number;
  externalId: string | null;
  name: string;
  role: string | null;
  details?: TimeReportsV2GroupDetails;
  items: TimeReportsV2Item[];
}

export interface GetTimeReportsV2Response {
  items: TimeReportsV2Group[];
  total: number;
}

export interface TimeReportsV2SummaryMetric {
  count: number;
  time: string;
}

export interface GetTimeReportsV2SummaryResponse {
  planned: TimeReportsV2SummaryMetric;
  approved: TimeReportsV2SummaryMetric;
  pending: TimeReportsV2SummaryMetric;
  open: TimeReportsV2SummaryMetric;
  absence: TimeReportsV2SummaryMetric;
  deviation: TimeReportsV2SummaryMetric;
}

export interface TimeReportV2Worker {
  id: number;
  firstName: string;
  lastName: string;
  externalId: string | null;
  role: string | null;
}

export interface TimeReportV2GigShift {
  id: number;
  startTime: string;
  endTime: string;
  breakStartTime: string | null;
  breakEndTime: string | null;
}

export interface TimeReportV2Gig {
  id: number;
  gigRole: string;
  gigType: GigType;
}

export interface TimeReportV2Company {
  id: number;
  companyName: string;
}

export interface TimeReportV2ApprovedByUser {
  id: number;
  firstName: string;
  lastName: string;
  externalId: string;
}

export interface TimeReportV2Structure {
  workGroupId: number;
  workGroupName: string;
  companyUnitId: number;
  companyUnitName: string;
  companyEntityId: number | null;
  companyEntityName: string | null;
}

export interface TimeReportV2Detail {
  id: number;
  status: TimeReportStatusV2;
  resolution: TimeReportResolution;
  paymentType: PaymentMethod;
  startTime: string | null;
  endTime: string | null;
  breakStartTime: string | null;
  breakEndTime: string | null;
  stampedStartTime: string | null;
  stampedEndTime: string | null;
  userNotes: string | null;
  companyNotes: string | null;
  createdAt: string;
  updatedAt: string;
  worker: TimeReportV2Worker;
  gigShift: TimeReportV2GigShift;
  gig: TimeReportV2Gig;
  company: TimeReportV2Company;
  approvedByUser: TimeReportV2ApprovedByUser | null;
  structure: TimeReportV2Structure;
}

export interface GetTimeReportV2CountResponse {
  value: number;
}

export interface StaffRegisterWorker {
  id: number;
  externalId: string | null;
  firstName: string;
  lastName: string;
  personalIdentityNumber: string | null;
}

export interface StaffRegisterItem {
  id: number;
  status: StaffRegisterStatus;
  reportedStartTime: string | null;
  reportedEndTime: string | null;
  stampStartTime: string | null;
  stampEndTime: string | null;
  stampedStartTime?: string | null;
  stampedEndTime?: string | null;
  plannedStartTime: string | null;
  plannedEndTime: string | null;
  worker: StaffRegisterWorker;
}

export type StaffRegisterStatus =
  | "New"
  | "Started"
  | "Stamped"
  | "TimeReported"
  | "Modified"
  | "NoStampOut";

export interface GetStaffRegisterResponse {
  items: StaffRegisterItem[];
  total: number;
}

export interface TimeReportResponse {
  companyUnitId: number | null;
  companyUnitName: string | null;
  workGroupId: number | null;
  workGroupName: string | null;
  workerId: number | null;
  workerName: string | null;
  gigId: number;
  gigType: string;
  gigShiftId: number;
  startTime: string;
  endTime: string;
  status: string[];
  timeReportIds: number[];
}

export interface ReportAbsenceResponse {
  companyUnitId: number | null;
  companyUnitName: string | null;
  workGroupId: number | null;
  workGroupName: string | null;
  workerId: number | null;
  workerName: string | null;
  gigId: number;
  gigType: string;
  gigShiftId: number;
  startTime: string;
  endTime: string;
  status: string[];
  timeReportIds: number[];
}

export interface ApproveTimeReportData {
  comment: string;
  isEditedByCompany: boolean;
  startTime: string;
  endTime: string;
  breakTime: number;
  breakStartTime: string;
  breakEndTime: string;
}

export interface EditTimeReportData {
  startTime: string;
  endTime: string;
  breakStartTime: string;
  breakEndTime: string;
}

const getTimeReportsV2 = async (
  companyId: number,
  fromDate: string,
  toDate: string,
  page?: number,
  pageSize?: number,
  sortBy?: TimeReportsV2SortBy,
  filters?: TimeReportsV2Filter[],
  workGroupIds?: number[],
  workerIds?: number[],
): Promise<AxiosResponse<GetTimeReportsV2Response>> => {
  const params: {
    page?: number;
    pageSize?: number;
    sortBy?: TimeReportsV2SortBy;
    filters?: string;
    workGroupIds?: string;
    workerIds?: string;
  } = {
    page,
    pageSize,
    sortBy,
    filters: filters && filters.length > 0 ? filters.join(",") : undefined,
    workGroupIds:
      workGroupIds && workGroupIds.length > 0
        ? workGroupIds.join(",")
        : undefined,
    workerIds:
      workerIds && workerIds.length > 0 ? workerIds.join(",") : undefined,
  };

  return await axiosRequest.auth.get<GetTimeReportsV2Response>(
    `${API_ROOT}/companies/${companyId}/timeReports/v2/${fromDate}-${toDate}`,
    {
      params,
    },
  );
};

const getTimeReportsV2Summary = async (
  companyId: number,
  fromDate: string,
  toDate: string,
  workGroupIds?: number[],
  workerIds?: number[],
): Promise<AxiosResponse<GetTimeReportsV2SummaryResponse>> => {
  const params: {
    workGroupIds?: string;
    workerIds?: string;
  } = {
    workGroupIds:
      workGroupIds && workGroupIds.length > 0
        ? workGroupIds.join(",")
        : undefined,
    workerIds:
      workerIds && workerIds.length > 0 ? workerIds.join(",") : undefined,
  };

  return await axiosRequest.auth.get<GetTimeReportsV2SummaryResponse>(
    `${API_ROOT}/companies/${companyId}/timeReports/v2/${fromDate}-${toDate}/summary`,
    {
      params,
    },
  );
};

const getTimeReportV2 = async (
  companyId: number,
  timeReportId: number,
): Promise<AxiosResponse<TimeReportV2Detail>> => {
  return await axiosRequest.auth.get<TimeReportV2Detail>(
    `${API_ROOT}/companies/${companyId}/timeReports/${timeReportId}/v2`,
  );
};

const reportAbsence = async (
  companyId: number,
  timeReportId: number,
  comment: string,
  resolution: AbsenceType,
): Promise<AxiosResponse<ReportAbsenceResponse>> => {
  return await axiosRequest.auth.put(
    `${API_ROOT}/companies/${companyId}/timeReports/${timeReportId}/absence`,
    {
      comment,
      resolution,
    },
  );
};

const withdrawTimeReport = async (
  companyId: number,
  timeReportId: number,
  companyNotes: string,
): Promise<AxiosResponse<TimeReportResponse>> => {
  return await axiosRequest.auth.put(
    `${API_ROOT}/companies/${companyId}/timeReports/${timeReportId}/withdraw`,
    {
      companyNotes,
    },
  );
};

const reopenTimeReport = async (
  companyId: number,
  timeReportId: number,
  companyNotes: string,
): Promise<AxiosResponse<TimeReportResponse>> => {
  return await axiosRequest.auth.put(
    `${API_ROOT}/companies/${companyId}/timeReports/${timeReportId}/reopen`,
    {
      companyNotes,
    },
  );
};

const rejectTimeReport = async (
  companyId: number,
  timeReportId: number,
  companyNotes: string,
): Promise<AxiosResponse<TimeReportResponse>> => {
  return await axiosRequest.auth.put(
    `${API_ROOT}/companies/${companyId}/timeReports/${timeReportId}/reject`,
    {
      companyNotes,
    },
  );
};

const approveTimeReport = async (
  companyId: number,
  timeReportId: number,
  data: ApproveTimeReportData,
): Promise<AxiosResponse<TimeReportResponse>> => {
  return await axiosRequest.auth.put(
    `${API_ROOT}/companies/${companyId}/timeReports/${timeReportId}/approve`,
    data,
  );
};

const editTimeReport = async (
  companyId: number,
  timeReportId: number,
  data: EditTimeReportData,
): Promise<AxiosResponse<TimeReportResponse>> => {
  return await axiosRequest.auth.put(
    `${API_ROOT}/companies/${companyId}/timeReports/${timeReportId}/v2`,
    data,
  );
};

const getTimeReportV2Count = async (
  companyId: number,
): Promise<AxiosResponse<GetTimeReportV2CountResponse>> => {
  return await axiosRequest.auth.get<GetTimeReportV2CountResponse>(
    `${API_ROOT}/companies/${companyId}/timeReports/v2/unhandled/count`,
  );
};

const getStaffRegister = async (
  companyId: number,
  fromDate: string,
  toDate: string,
): Promise<AxiosResponse<GetStaffRegisterResponse>> => {
  return await axiosRequest.auth.get<GetStaffRegisterResponse>(
    `${API_ROOT}/companies/${companyId}/timeReports/v2/${fromDate}-${toDate}/register`,
  );
};

const exportStaffRegister = async (
  companyId: number,
  fromDate: string,
  toDate: string,
  key?: string,
): Promise<AxiosResponse<Blob>> => {
  const keyQuery = key ? `?key=${encodeURIComponent(key)}` : "";
  return await axiosRequest.auth.post<Blob>(
    `${API_ROOT}/companies/${companyId}/export/timeReports/${fromDate}-${toDate}/register${keyQuery}`,
    undefined,
    {
      responseType: "blob",
    },
  );
};

export const timeReports = {
  getTimeReportsV2,
  getTimeReportsV2Summary,
  getTimeReportV2,
  reportAbsence,
  withdrawTimeReport,
  reopenTimeReport,
  rejectTimeReport,
  approveTimeReport,
  editTimeReport,
  getTimeReportV2Count,
  getStaffRegister,
  exportStaffRegister
};
